'use client';

import * as React from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';

type MediaLayer = { id: string; parentId: string | null };
type RegisterLayer = (id: string, parentId: string | null) => () => void;

const RegisterLayerContext = React.createContext<RegisterLayer | null>(null);
const ActiveLayerContext = React.createContext<string | null>(null);
const CurrentLayerContext = React.createContext<string | null>(null);

const fallbackRootLayers = new Set<string>();
const fallbackListeners = new Set<() => void>();

function registerFallbackRootLayer(id: string): () => void {
    fallbackRootLayers.add(id);
    fallbackListeners.forEach((listener) => listener());
    return () => {
        if (fallbackRootLayers.delete(id)) {
            fallbackListeners.forEach((listener) => listener());
        }
    };
}

function subscribeFallbackRootLayers(listener: () => void): () => void {
    fallbackListeners.add(listener);
    return () => {
        fallbackListeners.delete(listener);
    };
}

function getFallbackRootLayerCount(): number {
    return fallbackRootLayers.size;
}

/** 在页面内启用模态弹窗媒体隔离，未接入的页面保持原行为。 */
export function MediaActivityProvider({ children }: { children: React.ReactNode }) {
    const [layers, setLayers] = React.useState<MediaLayer[]>([]);
    const fallbackCount = React.useSyncExternalStore(
        subscribeFallbackRootLayers,
        getFallbackRootLayerCount,
        () => 0,
    );
    const register = React.useCallback<RegisterLayer>((id, parentId) => {
        setLayers((current) => {
            if (current.some((layer) => layer.id === id)) return current;
            // 嵌套弹窗的 layout effect 先于父层执行，父层需要插入已登记的子层之前。
            const childIndex = current.findIndex((layer) => layer.parentId === id);
            const next = [...current];
            next.splice(childIndex < 0 ? next.length : childIndex, 0, { id, parentId });
            return next;
        });
        return () => {
            setLayers((current) => (current.some((layer) => layer.id === id) ? current.filter((layer) => layer.id !== id) : current));
        };
    }, []);

    const activeLayerId = layers[layers.length - 1]?.id ?? (fallbackCount > 0 ? '__external_modal__' : null);

    return (
        <RegisterLayerContext.Provider value={register}>
            <ActiveLayerContext.Provider value={activeLayerId}>
                <CurrentLayerContext.Provider value={null}>{children}</CurrentLayerContext.Provider>
            </ActiveLayerContext.Provider>
        </RegisterLayerContext.Provider>
    );
}

/** 跟随弹窗内容的实际挂载生命周期，不提前恢复退出动画下方的媒体。 */
export function MediaDialogScope({ children, enabled = true, slottedContent, ...props }: React.ComponentProps<typeof Slot> & {
    enabled?: boolean;
    /** 保留 Content asChild 的宿主，Radix 附加的辅助节点仍作为其子节点传递。 */
    slottedContent?: React.ReactNode;
}) {
    const id = React.useId();
    const parentId = React.useContext(CurrentLayerContext);
    const register = React.useContext(RegisterLayerContext);

    React.useLayoutEffect(() => {
        if (!enabled) return;
        if (register) return register(id, parentId);
        return registerFallbackRootLayer(id);
    }, [enabled, id, parentId, register]);

    return <CurrentLayerContext.Provider value={enabled && register ? id : parentId}>
        {slottedContent !== undefined ? <Slot {...props}><Slottable>{slottedContent}</Slottable>{children}</Slot> : children}
    </CurrentLayerContext.Provider>;
}

export function useMediaSuspended() {
    const activeLayer = React.useContext(ActiveLayerContext);
    const currentLayer = React.useContext(CurrentLayerContext);
    return activeLayer !== null && activeLayer !== currentLayer;
}
